import { useState } from "react"
import Markdown from "react-markdown"
import { cn } from "@/lib/utils"
import { Pencil, Reply, ChevronDown, ChevronRight } from "lucide-react"

interface SuggestedAction {
  action: string
  target?: string
  tier: number
  reason: string
}

interface Message {
  role: "user" | "assistant" | "system" | "party"
  content: string
  meta?: {
    backend: string
    agents: string[]
    suggestedActions?: SuggestedAction[]
    confidence?: number
    signals?: string[]
    rationale?: string
    evidence?: string[]
  }
}

interface Props {
  message: Message
  onDrawItOut?: (content: string) => void
  onReaction?: (content: string, reaction: string) => void
  onReply?: (content: string, agentId?: string) => void
}

// Map agent IDs to their team names
const agentNames: Record<string, string> = {
  commander: "Commander",
  api: "Sentry",
  worker: "Forge",
  redis: "Cache",
  db: "Vault",
  railway: "Gateway",
  ui: "Pixel",
  ops: "Wrench",
  moderator: "Shield",
  labs: "Scope",
}

const REACTIONS = [
  { emoji: "\u{1F44D}", label: "thumbs up" },
  { emoji: "\u{1F44E}", label: "thumbs down" },
  { emoji: "\u{1F44F}", label: "clap" },
  { emoji: "\u{1F389}", label: "party" },
] as const

/** Blackboard-style code block for diagrams and ASCII art. */
function CodeBlock({ children, className }: { children?: React.ReactNode; className?: string }) {
  const lang = className?.replace("language-", "") || ""
  const isDiagram = ["diagram", "ascii", "text", ""].includes(lang)

  return (
    <div className={cn(
      "my-2 rounded-lg overflow-x-auto text-xs leading-relaxed font-mono",
      isDiagram
        ? "bg-[#1a2332] border border-indigo-500/20 p-3 text-indigo-200"
        : "bg-[#0f172a] border border-border p-3 text-gray-300"
    )}>
      <pre className="whitespace-pre">{children}</pre>
    </div>
  )
}

/** Structured investigation context — shows what Commander actually did */
function InvestigationContext({ meta }: { meta?: Message["meta"] }) {
  const [expanded, setExpanded] = useState(false)

  if (!meta) return null

  const hasAgents = meta.agents?.length > 0
  const hasSignals = meta.signals && meta.signals.length > 0
  const hasConfidence = meta.confidence != null && meta.confidence > 0
  const hasActions = meta.suggestedActions && meta.suggestedActions.length > 0
  const hasRationale = !!meta.rationale
  const hasAnything = hasAgents || hasSignals || hasConfidence || hasActions

  if (!hasAnything) return null

  const Chevron = expanded ? ChevronDown : ChevronRight

  const confidenceColor = !hasConfidence ? "" :
    meta.confidence! >= 0.8 ? "text-emerald-400" :
    meta.confidence! >= 0.6 ? "text-amber-400" :
    "text-red-400"

  const confidenceBg = !hasConfidence ? "" :
    meta.confidence! >= 0.8 ? "bg-emerald-500/15" :
    meta.confidence! >= 0.6 ? "bg-amber-500/15" :
    "bg-red-500/15"

  return (
    <div className="mt-2 pt-2 border-t border-border/30">
      {/* Summary line — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full text-left hover:bg-surface/30 rounded px-1 py-0.5 -mx-1 transition-colors"
      >
        <Chevron size={10} className="text-gray-600 shrink-0" />
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {/* Agents consulted */}
          {meta.agents.map(id => (
            <span key={id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface text-gray-400 font-mono">
              {agentNames[id] || id}
            </span>
          ))}
          {/* Confidence badge */}
          {hasConfidence && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${confidenceBg} ${confidenceColor}`}>
              {Math.round(meta.confidence! * 100)}%
            </span>
          )}
          {/* Signal count hint */}
          {hasSignals && !expanded && (
            <span className="text-[10px] text-gray-600">
              {meta.signals!.length} signal{meta.signals!.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-1.5 space-y-1.5 pl-4">
          {/* Signals */}
          {hasSignals && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-gray-600 shrink-0">Detected:</span>
              {meta.signals!.map(sig => (
                <span key={sig} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 font-mono">
                  {sig.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}

          {/* Rationale */}
          {hasRationale && (
            <div className="text-[10px] text-gray-500 leading-relaxed">
              {meta.rationale}
            </div>
          )}

          {/* Suggested actions */}
          {hasActions && (
            <div className="space-y-1">
              <span className="text-[10px] text-gray-600">Suggested actions:</span>
              {meta.suggestedActions!.map((sa, i) => {
                const tierColor = sa.tier === 1 ? "bg-emerald-500/15 text-emerald-400" :
                                  sa.tier === 2 ? "bg-amber-500/15 text-amber-400" :
                                  "bg-red-500/15 text-red-400"
                const tierLabel = sa.tier === 1 ? "Auto" : sa.tier === 2 ? "5m window" : "Needs OK"
                return (
                  <div key={i} className="flex items-center gap-1.5 text-[10px]">
                    <span className={`px-1 py-0.5 rounded font-bold shrink-0 ${tierColor}`}>{tierLabel}</span>
                    <span className="text-gray-300 font-mono">{sa.action}</span>
                    {sa.target && <span className="text-gray-600">→ {sa.target}</span>}
                    <span className="text-gray-600 truncate" title={sa.reason}>{sa.reason}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Reaction bar + draw-it-out + reply — shows on hover for assistant messages. */
function ActionBar({ content, onDrawItOut, onReaction, onReply, agents }: {
  content: string
  onDrawItOut?: (content: string) => void
  onReaction?: (content: string, reaction: string) => void
  onReply?: (content: string, agentId?: string) => void
  agents?: string[]
}) {
  const [picked, setPicked] = useState<string | null>(null)

  const handleReaction = (emoji: string) => {
    setPicked(emoji)
    onReaction?.(content, emoji)
  }

  return (
    <div className="mt-1.5 flex items-center gap-1 flex-wrap">
      {/* Reactions */}
      {picked ? (
        <span className="text-sm leading-none">{picked}</span>
      ) : (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {REACTIONS.map(r => (
            <button
              key={r.label}
              onClick={() => handleReaction(r.emoji)}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-surface/80 transition-colors text-sm leading-none"
              title={r.label}
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}

      {/* Reply button */}
      {onReply && (
        <button
          onClick={() => onReply(content)}
          className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-gray-500 hover:text-blue-400 hover:bg-blue-500/10"
          title="Reply to this message"
        >
          <Reply size={10} />
          <span>reply</span>
        </button>
      )}

      {/* Reply to specific agent — when multiple agents contributed */}
      {onReply && agents && agents.length > 1 && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 ml-0.5">
          {agents.map(id => {
            const name = agentNames[id] || id
            return (
              <button
                key={id}
                onClick={() => onReply(content, id)}
                className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] text-gray-600 hover:text-white hover:bg-surface/80 transition-colors"
                title={`Reply to @${name}`}
              >
                <Reply size={8} />
                {name}
              </button>
            )
          })}
        </div>
      )}

      {/* Draw it out button */}
      {onDrawItOut && (
        <button
          onClick={() => onDrawItOut(content)}
          className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-gray-500 hover:text-indigo-400 hover:bg-indigo-500/10"
          title="Ask agents to draw a visual diagram"
        >
          <Pencil size={10} />
          <span>draw it out</span>
        </button>
      )}
    </div>
  )
}

export default function MessageBubble({ message, onDrawItOut, onReaction, onReply }: Props) {
  const { role, content, meta } = message

  if (role === "system") {
    return (
      <div className="text-center text-gray-600 text-xs italic py-2">
        {content}
      </div>
    )
  }

  // Detect if response contains markdown (code blocks, headers, lists, bold)
  const hasMarkdown = role === "assistant" && /```|^#+\s|^\s*[-*]\s|\*\*/.test(content)

  return (
    <div className={cn("flex group min-w-0", role === "user" ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] min-w-0 px-3.5 py-2.5 text-sm leading-relaxed break-words",
          role === "user"
            ? "bg-accent text-white rounded-2xl rounded-br-sm whitespace-pre-wrap"
            : "bg-card text-gray-200 border border-border rounded-2xl rounded-bl-sm",
          // When rendering markdown, let Markdown handle spacing
          !hasMarkdown && role === "assistant" && "whitespace-pre-wrap"
        )}
      >
        {hasMarkdown ? (
          <Markdown
            components={{
              code({ className, children, ...props }) {
                const lang = className?.replace("language-", "") || ""
                // Diagrams (ASCII art, mermaid fallback) — render as blackboard block
                const isDiagramBlock = ["mermaid", "diagram", "ascii"].includes(lang)
                if (isDiagramBlock) {
                  return <CodeBlock className="language-diagram">{children}</CodeBlock>
                }
                // Inline code vs block
                const isBlock = /\n/.test(String(children))
                if (isBlock) {
                  return <CodeBlock className={className}>{children}</CodeBlock>
                }
                return (
                  <code className="bg-surface px-1 py-0.5 rounded text-xs font-mono text-indigo-300" {...props}>
                    {children}
                  </code>
                )
              },
              pre({ children }) {
                // react-markdown wraps code blocks in <pre><code>, we handle in code()
                return <>{children}</>
              },
              h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1 text-white">{children}</h1>,
              h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1 text-white">{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-semibold mt-1.5 mb-0.5 text-gray-200">{children}</h3>,
              p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="list-disc list-inside mb-1.5 space-y-0.5">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside mb-1.5 space-y-0.5">{children}</ol>,
              strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
            }}
          >
            {content}
          </Markdown>
        ) : (
          content
        )}
        {/* Investigation context — surfaces the structured data from Commander */}
        <InvestigationContext meta={meta} />
        {role === "assistant" && (
          <ActionBar
            content={content}
            onDrawItOut={onDrawItOut}
            onReaction={onReaction}
            onReply={onReply}
            agents={meta?.agents}
          />
        )}
      </div>
    </div>
  )
}
